import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import OperationsTheme from "../components/shared/OperationsTheme";
import useContratosAtivos from "../hooks/useContratosAtivos";
import { CLASSIFICACAO_LABELS, suporteApi } from "../features/suporte/services/suporteApi";

const statusInfo = {
  NOVO: { label: "Aberto", color: "info" },
  EM_TRIAGEM: { label: "Em análise", color: "warning" },
  EM_ATENDIMENTO: { label: "Em atendimento", color: "primary" },
  AGUARDANDO: { label: "Aguardando você", color: "warning" },
  RESOLVIDO: { label: "Resolvido", color: "success" },
  ENCERRADO: { label: "Concluído", color: "default" },
  CANCELADO: { label: "Cancelado", color: "default" },
};

const prioridadeInfo = {
  CRITICA: { label: "Crítica", color: "error" },
  ALTA: { label: "Alta", color: "warning" },
  NORMAL: { label: "Normal", color: "default" },
  BAIXA: { label: "Baixa", color: "default" },
};

const filtros = [
  { label: "Abertos", value: "abertos" },
  { label: "Aguardando retorno", value: "aguardando" },
  { label: "Resolvidos", value: "resolvidos" },
  { label: "Concluídos", value: "concluidos" },
  { label: "Todos", value: "todos" },
];

const isAberto = (status) => !["RESOLVIDO", "ENCERRADO", "CANCELADO"].includes(status);

const cardSx = (theme) => ({
  border: `1px solid var(--ops-border, ${theme.palette.divider})`,
  borderRadius: 1.8,
  bgcolor: "var(--ops-surface, #fff)",
  boxShadow: "0 1px 3px rgba(31, 58, 86, 0.035)",
});

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium", timeStyle: "short" }).format(date);
};

const formatDateShort = (value) => {
  if (!value) return "—";
  const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date);
};

const mapResumo = (item) => ({ ...item, produto: item.nomeProduto || "Geral", abertoEm: formatDate(item.dataAbertura), atualizadoEm: formatDate(item.dataUltimaAlteracao), mensagens: [] });

const mapDetalhe = (item, historico) => {
  const mensagens = [];
  if (item.descricao) mensagens.push({ autor: item.solicitanteNome || "Você", tipo: "cliente", data: formatDate(item.dataAbertura), texto: item.descricao });
  (historico?.itens || []).forEach((entry) => mensagens.push({ autor: entry.nomeAutor || (entry.tipoAutor === "CLIENTE" ? "Cliente" : "Suporte"), tipo: entry.tipoAutor === "CLIENTE" ? "cliente" : "suporte", data: formatDate(entry.dataRegistro), texto: entry.mensagem }));
  return { ...mapResumo(item), mensagens };
};

export default function Suporte() {
  const { contratosAtivos = [] } = useContratosAtivos();
  const [chamados, setChamados] = useState([]);
  const [total, setTotal] = useState(0);
  const [estatisticas, setEstatisticas] = useState({ abertos: 0, aguardando: 0, resolvidos: 0, concluidos: 0 });
  const [classificacoes, setClassificacoes] = useState(Object.keys(CLASSIFICACAO_LABELS));
  const [tela, setTela] = useState("lista");
  const [selecionado, setSelecionado] = useState(null);
  const [filtro, setFiltro] = useState("abertos");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [form, setForm] = useState({ idProduto: "", classificacao: "", assunto: "", descricao: "" });
  const [editando, setEditando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [aviso, setAviso] = useState("");
  const [confirmarConclusao, setConfirmarConclusao] = useState(false);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  const [atualizacao, setAtualizacao] = useState(0);

  const produtos = useMemo(() => {
    const unicos = new Map();
    contratosAtivos.forEach((contrato) => {
      const id = contrato.idProduto ?? contrato.id_produto;
      const nome = contrato.nomeProduto ?? contrato.nome_produto;
      if (id != null && nome) unicos.set(String(id), { id: String(id), nome });
    });
    return [...unicos.values()].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [contratosAtivos]);

  const carregarLista = useCallback(async (signal) => {
    setCarregando(true);
    setErro("");
    const params = {
      ...(filtro === "abertos" ? { grupo: "ABERTOS" } : {}),
      ...(filtro === "aguardando" ? { status: "AGUARDANDO" } : {}),
      ...(filtro === "resolvidos" ? { status: "RESOLVIDO" } : {}),
      ...(filtro === "concluidos" ? { status: "ENCERRADO" } : {}),
      ...(filtro === "todos" ? { grupo: "TODOS" } : {}),
      busca: busca.trim() || undefined,
      pagina,
      tamanho: pageSize,
    };
    try {
      const [resultado, abertos, aguardando, resolvidos, concluidos] = await Promise.all([
        suporteApi.listar(params, signal),
        suporteApi.listar({ grupo: "ABERTOS", pagina: 0, tamanho: 1 }, signal),
        suporteApi.listar({ status: "AGUARDANDO", pagina: 0, tamanho: 1 }, signal),
        suporteApi.listar({ status: "RESOLVIDO", pagina: 0, tamanho: 1 }, signal),
        suporteApi.listar({ status: "ENCERRADO", pagina: 0, tamanho: 1 }, signal),
      ]);
      setChamados((resultado.itens || []).map(mapResumo));
      setTotal(resultado.total || 0);
      setEstatisticas({ abertos: abertos.total || 0, aguardando: aguardando.total || 0, resolvidos: resolvidos.total || 0, concluidos: concluidos.total || 0 });
    } catch (requestError) {
      if (requestError.name !== "CanceledError" && requestError.name !== "AbortError") setErro(requestError.message);
    } finally {
      if (!signal?.aborted) setCarregando(false);
    }
  }, [busca, filtro, pagina, pageSize]);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => carregarLista(controller.signal), 250);
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [carregarLista, atualizacao]);

  useEffect(() => {
    const controller = new AbortController();
    suporteApi.opcoes(controller.signal)
      .then((opcoes) => { if (Array.isArray(opcoes.classificacoes)) setClassificacoes(opcoes.classificacoes); })
      .catch((requestError) => { if (requestError.name !== "CanceledError" && requestError.name !== "AbortError") setErro(requestError.message); });
    return () => controller.abort();
  }, []);

  const voltarParaLista = () => {
    setTela("lista"); setSelecionado(null); setConfirmacao(""); setAviso(""); setErro(""); setMensagem("");
  };

  const abrirDetalhe = async (chamado) => {
    setSelecionado(chamado); setTela("detalhe"); setErro(""); setAviso(""); setConfirmacao(""); setCarregandoDetalhe(true);
    try {
      const [detalhe, historico] = await Promise.all([
        suporteApi.detalhe(chamado.protocolo),
        suporteApi.historico(chamado.protocolo, { pagina: 0, tamanho: 100 }),
      ]);
      setSelecionado(mapDetalhe(detalhe, historico));
    } catch (requestError) { setErro(requestError.message); }
    finally { setCarregandoDetalhe(false); }
  };

  const iniciarEdicao = () => {
    if (!selecionado) return;
    setForm({ idProduto: selecionado.idProduto == null ? "" : String(selecionado.idProduto), classificacao: selecionado.classificacao || "", assunto: selecionado.assunto || "", descricao: selecionado.descricao || "" });
    setEditando(true); setConfirmacao(""); setErro(""); setTela("novo");
  };

  const iniciarNovo = () => {
    setForm({ idProduto: "", classificacao: "", assunto: "", descricao: "" });
    setEditando(false); setConfirmacao(""); setErro(""); setTela("novo");
  };

  const salvarChamado = async (event) => {
    event.preventDefault(); setSalvando(true); setErro("");
    const dados = { idProduto: form.idProduto ? Number(form.idProduto) : null, idClienteIntegracao: null, classificacao: form.classificacao || null, assunto: form.assunto.trim(), descricao: form.descricao.trim() };
    try {
      const resposta = editando
        ? await suporteApi.atualizar({ ...dados, protocolo: selecionado.protocolo, versao: selecionado.versao })
        : await suporteApi.abrir(dados);
      setAtualizacao((value) => value + 1);
      await abrirDetalhe(resposta);
      setConfirmacao(editando ? "Chamado atualizado." : "Seu chamado foi registrado. Você pode acompanhar as atualizações nesta página.");
      setEditando(false); setForm({ idProduto: "", classificacao: "", assunto: "", descricao: "" });
    } catch (requestError) { setErro(requestError.message); }
    finally { setSalvando(false); }
  };

  const enviarMensagem = async (event) => {
    event.preventDefault();
    if (!mensagem.trim() || !selecionado) return;
    setSalvando(true); setErro("");
    try {
      await suporteApi.interagir({ protocolo: selecionado.protocolo, mensagem: mensagem.trim() });
      setMensagem(""); setAtualizacao((value) => value + 1);
      await abrirDetalhe(selecionado);
      setConfirmacao("Mensagem adicionada ao histórico do chamado.");
    } catch (requestError) { setErro(requestError.message); }
    finally { setSalvando(false); }
  };

  const concluirChamado = async () => {
    if (!selecionado || selecionado.status !== "RESOLVIDO") return;
    setConcluindo(true);
    setErro("");
    setAviso("");
    try {
      const atualizado = await suporteApi.concluir({
        protocolo: selecionado.protocolo,
        versao: selecionado.versao,
      });
      setSelecionado((current) => ({
        ...current,
        ...mapResumo(atualizado),
        mensagens: current?.mensagens || [],
      }));
      setConfirmarConclusao(false);
      setConfirmacao("Chamado concluído.");
      setAtualizacao((value) => value + 1);
    } catch (requestError) {
      setConfirmarConclusao(false);
      if (requestError.status === 409) {
        let detalheAtualizado = false;
        try {
          const [detalhe, historico] = await Promise.all([
            suporteApi.detalhe(selecionado.protocolo),
            suporteApi.historico(selecionado.protocolo, { pagina: 0, tamanho: 100 }),
          ]);
          setSelecionado(mapDetalhe(detalhe, historico));
          setAtualizacao((value) => value + 1);
          detalheAtualizado = true;
        } catch (refreshError) {
          setErro(refreshError.message);
        }
        setAviso(detalheAtualizado
          ? "O chamado foi alterado desde que você abriu esta tela. Atualizamos os dados; confira o status e tente novamente se ainda estiver resolvido."
          : "O chamado foi alterado desde que você abriu esta tela, mas não conseguimos atualizar os detalhes. Volte à lista e abra o chamado novamente.");
      } else {
        setErro(requestError.message);
      }
    } finally {
      setConcluindo(false);
    }
  };

  return (
    <OperationsTheme>
      <Box sx={{
        mx: { xs: -2, md: -3 },
        my: -3,
        px: { xs: 2, md: 3 },
        pt: { xs: 1.25, md: 1.6 },
        pb: 2,
        minHeight: "100%",
        color: "var(--ops-text)",
        background: "linear-gradient(180deg, var(--ops-canvas) 0%, var(--ops-canvas-end) 100%)",
        "& .MuiButton-root": { fontSize: 11.5, minHeight: 30, fontWeight: 650, boxShadow: "none" },
        "& .MuiChip-root": { height: 23, fontSize: 10.5, maxWidth: "100%" },
        "& .MuiInputBase-root": { fontSize: 12 },
        "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": { minHeight: 36 },
        "& .MuiOutlinedInput-input": { py: 0.9 },
        "& .MuiInputLabel-root": { fontSize: 12 },
        "& .MuiTypography-h6": { fontSize: 15, lineHeight: 1.25 },
        "& .MuiTypography-body2": { fontSize: 11.5, lineHeight: 1.4 },
        "& .MuiTypography-caption": { fontSize: 10.5, lineHeight: 1.35 },
        "& .MuiTablePagination-root": { minHeight: 42 },
        "& .MuiTablePagination-toolbar": { minHeight: 42, px: 1.25 },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: 11.5 },
        "& .MuiAlert-root": { py: 0.5, px: 1.25, fontSize: 11.5, borderRadius: 1.5 },
        "& .MuiAlert-message": { py: 0.5 },
        "& .MuiAlert-icon": { fontSize: 18, py: 0.5, mr: 1 },
      }}>
    <Stack spacing={{ xs: 1.5, md: 1.6 }} sx={{ pb: 2 }}>
      {erro && <Alert severity="error" onClose={() => setErro("")}>{erro}</Alert>}
      {aviso && <Alert severity="warning" onClose={() => setAviso("")}>{aviso}</Alert>}
      {carregando && tela === "lista" && <Alert severity="info" icon={<CircularProgress size={18} />}>Carregando seus chamados…</Alert>}
      {tela === "lista" && <ListaChamados chamados={chamados} total={total} estatisticas={estatisticas} pagina={pagina} pageSize={pageSize} filtro={filtro} busca={busca} carregando={carregando} erro={Boolean(erro)} onPagina={setPagina} onPageSize={(value) => { setPageSize(value); setPagina(0); }} onFiltro={(value) => { setChamados([]); setTotal(0); setCarregando(true); setErro(""); setFiltro(value); setPagina(0); }} onBusca={(value) => { setBusca(value); setPagina(0); }} onNovo={iniciarNovo} onAbrir={abrirDetalhe} />}
      {tela === "novo" && <NovoChamado form={form} setForm={setForm} produtos={produtos} classificacoes={classificacoes} editando={editando} salvando={salvando} onVoltar={voltarParaLista} onSubmit={salvarChamado} />}
      {tela === "detalhe" && selecionado && (carregandoDetalhe
        ? <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
        : <DetalheChamado chamado={selecionado} confirmacao={confirmacao} mensagem={mensagem} salvando={salvando} concluindo={concluindo} confirmarConclusao={confirmarConclusao} setConfirmarConclusao={setConfirmarConclusao} setMensagem={setMensagem} onEditar={iniciarEdicao} onVoltar={voltarParaLista} onSubmit={enviarMensagem} onNovo={iniciarNovo} onConcluir={concluirChamado} />)}
    </Stack>
      </Box>
    </OperationsTheme>
  );
}
function Cabecalho({ titulo, descricao, acao, voltar, codigo }) {
  return (
    <Stack spacing={0.75}>
      {voltar && <Button size="small" onClick={voltar} startIcon={<ArrowBackRounded />} sx={{ alignSelf: "flex-start", ml: -0.75, color: "var(--ops-accent)" }}>Voltar para chamados</Button>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
        {codigo && (
          <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), flexShrink: 0, minWidth: 150, px: 1.35, py: 0.75, borderColor: "var(--ops-accent)", bgcolor: "var(--ops-accent)", color: "#fff" })}>
            <Typography variant="overline" sx={{ display: "block", lineHeight: 1.2, opacity: 0.82, fontWeight: 700 }}>Código do chamado</Typography>
            <Typography variant="h5" sx={{ mt: 0.35, fontWeight: 850, fontFamily: "monospace", letterSpacing: "0.04em" }}>{codigo}</Typography>
          </Paper>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h1" sx={{ color: "var(--ops-heading)", fontSize: { xs: 21, md: 23 }, fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.15 }}>{titulo}</Typography>
          <Typography sx={{ color: "var(--ops-muted)", mt: 0.2, fontSize: 13, lineHeight: 1.3 }}>{descricao}</Typography>
        </Box>
        {acao}
      </Stack>
    </Stack>
  );
}

function ListaChamados({ chamados, total, estatisticas, pagina, pageSize, filtro, busca, carregando, erro, onPagina, onPageSize, onFiltro, onBusca, onNovo, onAbrir }) {
  return (
    <Stack spacing={1.25}>
      <Cabecalho
        titulo="Suporte"
        descricao="Abra solicitações e acompanhe as conversas com nossa equipe."
        acao={<Button variant="contained" size="small" startIcon={<AddRounded />} onClick={onNovo} sx={{ minHeight: 34, px: 1.4, borderRadius: 1.5, whiteSpace: "nowrap" }}>Abrir chamado</Button>}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 0.75 }}>
        <Resumo label="Abertos" valor={estatisticas.abertos} icon={<AccessTimeRounded />} tom="primary" />
        <Resumo label="Aguardando seu retorno" valor={estatisticas.aguardando} icon={<SupportAgentRounded />} tom="warning" />
        <Resumo label="Resolvidos" valor={estatisticas.resolvidos} icon={<CheckCircleOutlineRounded />} tom="success" />
        <Resumo label="Concluídos" valor={estatisticas.concluidos} icon={<CheckCircleOutlineRounded />} tom="success" />
      </Box>

      <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), overflow: "hidden" })}>
        <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="space-between" gap={0.75} sx={{ px: { xs: 1, md: 1.25 }, pt: 0.6 }}>
          <Tabs value={filtro} onChange={(_, value) => onFiltro(value)} variant="scrollable" scrollButtons="auto" aria-label="Filtrar chamados"
            sx={{ minHeight: 38, "& .MuiTab-root": { minHeight: 38, minWidth: "auto", px: 1.15, fontSize: 12, textTransform: "none", fontWeight: 700 } }}>
            {filtros.map((item) => <Tab key={item.value} value={item.value} label={item.label} />)}
          </Tabs>
          <TextField
            size="small"
            placeholder="Buscar por assunto ou protocolo"
            value={busca}
            onChange={(event) => onBusca(event.target.value)}
            sx={{ width: { xs: "100%", md: 300 }, mb: { md: 0.6 } }}
            inputProps={{ "aria-label": "Buscar chamados por assunto ou protocolo" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" color="action" /></InputAdornment> }}
          />
        </Stack>
        <Divider />
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table size="small" aria-label="Lista de chamados" sx={{ minWidth: 850, "& .MuiTableCell-root": { borderColor: "var(--ops-border)", px: 1, py: 0.7, fontSize: 11.5, color: "var(--ops-text)" }, "& thead .MuiTableCell-root": { bgcolor: "var(--ops-subtle)", color: "var(--ops-muted)", fontWeight: 800, lineHeight: 1.3 }, "& tbody .MuiTableRow-root:hover": { bgcolor: "var(--ops-hover)" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 290, fontWeight: 800 }}>Protocolo / assunto</TableCell>
                <TableCell sx={{ minWidth: 150, fontWeight: 800 }}>Produto</TableCell>
                <TableCell sx={{ minWidth: 130, fontWeight: 800 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 120, fontWeight: 800 }}>Prioridade</TableCell>
                <TableCell sx={{ minWidth: 165, fontWeight: 800 }}>Abertura</TableCell>
                <TableCell sx={{ minWidth: 185, fontWeight: 800 }}>Última interação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chamados.length ? chamados.map((chamado) => <ChamadoTableRow key={chamado.protocolo} chamado={chamado} onAbrir={onAbrir} />) : carregando ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : erro ? null : (
                <TableRow><TableCell colSpan={6}>
                  <Stack spacing={1} alignItems="center" sx={{ py: 5, textAlign: "center" }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: "action.hover", color: "text.secondary" }}><SupportAgentRounded /></Avatar>
                    <Typography fontWeight={700}>{busca ? "Nenhum chamado encontrado" : "Nenhum chamado nesta lista"}</Typography>
                    <Typography variant="body2" color="text.secondary">{busca ? "Tente buscar por outro assunto ou protocolo." : "Quando você abrir um chamado, ele aparecerá aqui."}</Typography>
                    {!busca && <Button onClick={onNovo} startIcon={<AddRounded />}>Abrir chamado</Button>}
                  </Stack>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={pagina}
          onPageChange={(_, value) => onPagina(value)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => onPageSize(Number(event.target.value))}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>
    </Stack>
  );
}
function Resumo({ label, valor, icon, tom }) {
  const tons = {
    primary: "var(--ops-accent)",
    warning: "var(--ops-warning)",
    success: "var(--ops-success)",
  };
  const color = tons[tom] || tons.primary;
  return (
    <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), px: 1, py: 0.7, minWidth: 0, minHeight: 54, borderTop: `2px solid ${color}` })}>
      <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minHeight: 38 }}>
        {cloneElement(icon, { sx: { color, fontSize: 16, flexShrink: 0 } })}
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 0.5, flex: 1 }}>
          <Typography variant="caption" sx={{ color: "var(--ops-muted)", fontWeight: 750, lineHeight: 1.2, fontSize: 10.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Typography>
          <Typography sx={{ fontWeight: 850, color: tom === "primary" ? "var(--ops-heading)" : color, fontSize: 18, lineHeight: 1, letterSpacing: "-0.02em", flexShrink: 0 }}>{valor}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function ChamadoTableRow({ chamado, onAbrir }) {
  const status = statusInfo[chamado.status] || statusInfo.NOVO;
  const prioridade = prioridadeInfo[chamado.prioridade] || prioridadeInfo.NORMAL;
  const ultimaInteracao = chamado.dataUltimaInteracaoPublica || chamado.dataAbertura;
  return (
    <TableRow hover onClick={() => onAbrir(chamado)} sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: "var(--ops-hover)" } }}>
      <TableCell>
        <Stack spacing={0.15} alignItems="flex-start">
          <Button onClick={(event) => { event.stopPropagation(); onAbrir(chamado); }} sx={{ minWidth: 0, p: 0, fontSize: 11.5, lineHeight: 1.3, fontWeight: 800, color: "var(--ops-accent)", textDecoration: "none", textTransform: "none" }}>
            {chamado.protocolo}
          </Button>
          <Typography variant="body2" fontWeight={650} sx={{ fontSize: 11.5, lineHeight: 1.35, overflowWrap: "anywhere" }}>{chamado.assunto}</Typography>
          <Typography variant="caption" color="text.secondary">{CLASSIFICACAO_LABELS[chamado.classificacao] || chamado.classificacao || "Sem classificação"}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 11.5 }}>{chamado.produto}</Typography>
      </TableCell>
      <TableCell><Chip size="small" color={status.color} label={status.label} sx={{ height: 22, fontSize: 10.5, fontWeight: 750 }} /></TableCell>
      <TableCell><Chip size="small" variant="outlined" color={prioridade.color} label={prioridade.label} sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }} /></TableCell>
      <TableCell><Typography variant="body2" sx={{ fontSize: 11.5, whiteSpace: "nowrap" }}>{formatDateShort(chamado.dataAbertura)}</Typography></TableCell>
      <TableCell><Typography variant="body2" sx={{ fontSize: 11.5, whiteSpace: "nowrap" }}>{formatDateShort(ultimaInteracao)}</Typography></TableCell>
    </TableRow>
  );
}
function NovoChamado({ form, setForm, produtos, classificacoes, editando, salvando, onVoltar, onSubmit }) {
  const atualizar = (campo) => (event) => setForm((atual) => ({ ...atual, [campo]: event.target.value }));
  const valido = form.assunto.trim() && form.descricao.trim();
  return (
    <Stack spacing={1.25}>
      <Cabecalho titulo={editando ? "Editar chamado" : "Abrir chamado"} descricao="Conte para nós como podemos ajudar. Nossa equipe responderá por aqui." voltar={onVoltar} />
      <Box component="form" onSubmit={onSubmit}>
        <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), p: { xs: 1.5, md: 1.75 } })}>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="h6" fontWeight={750}>Sobre sua solicitação</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>Os campos com * são necessários para registrar o chamado.</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
              <TextField size="small" select label="Produto relacionado" value={form.idProduto} onChange={atualizar("idProduto")}>
                <MenuItem value="">Não se aplica</MenuItem>
                {produtos.map((produto) => <MenuItem key={produto.id} value={produto.id}>{produto.nome}</MenuItem>)}
              </TextField>
              <TextField select label="Tipo de solicitação" value={form.classificacao} onChange={atualizar("classificacao")}>
                <MenuItem value="">Não informado</MenuItem>
                {classificacoes.map((item) => <MenuItem key={item} value={item}>{CLASSIFICACAO_LABELS[item] || item}</MenuItem>)}
              </TextField>
              <TextField size="small" required label="Assunto" value={form.assunto} onChange={atualizar("assunto")} inputProps={{ maxLength: 256 }} sx={{ gridColumn: "1 / -1" }} />
              <TextField required label="Descreva sua necessidade" value={form.descricao} onChange={atualizar("descricao")} multiline minRows={5} inputProps={{ maxLength: 20000 }} helperText="Inclua detalhes que ajudem nossa equipe a entender e resolver sua solicitação." sx={{ gridColumn: "1 / -1" }} />
            </Box>
            <Divider />
            <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="space-between" gap={1}>
              <Button onClick={onVoltar} startIcon={<ArrowBackRounded />}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={!valido || salvando} endIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <ChevronRightRounded />} sx={{ px: 2.5 }}>{salvando ? "Salvando…" : editando ? "Salvar alterações" : "Enviar chamado"}</Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}

function DetalheChamado({ chamado, confirmacao, mensagem, salvando, concluindo, confirmarConclusao, setConfirmarConclusao, setMensagem, onEditar, onVoltar, onSubmit, onNovo, onConcluir }) {
  const status = statusInfo[chamado.status] || statusInfo.NOVO;
  const permiteResposta = isAberto(chamado.status);
  return (
    <Stack spacing={1.25}>
      <Cabecalho titulo={chamado.assunto} descricao={`${chamado.produto} · Aberto em ${chamado.abertoEm}`} codigo={chamado.protocolo} voltar={onVoltar} />
      {confirmacao && <Alert severity="success">{confirmacao}</Alert>}

      <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), p: { xs: 1.2, md: 1.5 } })}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>Status do chamado</Typography>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 0.15 }}>
              <Chip size="small" color={status.color} label={status.label} sx={{ fontWeight: 750 }} />
              <Typography variant="body2" color="text.secondary">Última atualização: {chamado.atualizadoEm}</Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {chamado.status === "RESOLVIDO" && <Button variant="contained" size="small" startIcon={<CheckCircleOutlineRounded />} onClick={() => setConfirmarConclusao(true)}>Concluir chamado</Button>}
            {chamado.status === "NOVO" && <Button onClick={onEditar}>Editar chamado</Button>}
            <Button onClick={onVoltar} startIcon={<ArrowBackRounded />}>Todos os chamados</Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={confirmarConclusao}
        onClose={() => { if (!concluindo) setConfirmarConclusao(false); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Concluir chamado?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirma a conclusão do chamado {chamado.protocolo}? Depois de concluído, ele não aceitará novas mensagens.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarConclusao(false)} disabled={concluindo}>Cancelar</Button>
          <Button variant="contained" onClick={onConcluir} disabled={concluindo}>
            {concluindo ? <CircularProgress size={18} color="inherit" /> : "Concluir chamado"}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), p: { xs: 1.2, md: 1.5 } })}>
        <Typography variant="h6" fontWeight={750} sx={{ mb: 1 }}>Conversa</Typography>
        <Stack spacing={1.25}>
          {chamado.mensagens.map((item, index) => <Mensagem key={`${item.data}-${index}`} mensagem={item} />)}
        </Stack>
        {permiteResposta ? <>
          <Divider sx={{ my: 1.5 }} />
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={1}>
              <TextField label="Escreva uma mensagem" placeholder="Digite sua resposta ou envie mais informações…" value={mensagem} onChange={(event) => setMensagem(event.target.value)} multiline minRows={3} inputProps={{ maxLength: 5000 }} />
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" gap={1}>
                <Typography variant="caption" color="text.secondary">Sua mensagem será adicionada à conversa deste chamado.</Typography>
                <Button type="submit" variant="contained" disabled={!mensagem.trim() || salvando} endIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <SendRounded />}>{salvando ? "Enviando…" : "Enviar mensagem"}</Button>
              </Stack>
            </Stack>
          </Box>
        </> : <Alert severity="success" sx={{ mt: 2.5 }} icon={<CheckCircleOutlineRounded />}>
          Este chamado foi resolvido. Se precisar de mais ajuda, você pode abrir uma nova solicitação.
          <Button size="small" onClick={onNovo} sx={{ ml: { sm: 1 } }}>Abrir novo chamado</Button>
        </Alert>}
      </Paper>
    </Stack>
  );
}

function Mensagem({ mensagem }) {
  const suporte = mensagem.tipo === "suporte";
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ maxWidth: { md: "86%" }, alignSelf: suporte ? "flex-start" : "flex-end", flexDirection: suporte ? "row" : "row-reverse" }}>
      <Avatar sx={{ width: 30, height: 30, bgcolor: suporte ? "var(--ops-accent)" : "var(--ops-selected)", color: "#fff", fontSize: 11, fontWeight: 800 }}>
        {suporte ? <SupportAgentRounded fontSize="small" /> : "VC"}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="baseline" justifyContent={suporte ? "flex-start" : "flex-end"} flexWrap="wrap">
          <Typography variant="body2" fontWeight={750} sx={{ fontSize: 11.5 }}>{mensagem.autor}</Typography>
          <Typography variant="caption" sx={{ color: "var(--ops-muted)", fontSize: 10.5 }}>{mensagem.data}</Typography>
        </Stack>
        <Paper elevation={0} sx={(theme) => ({ ...cardSx(theme), mt: 0.45, px: 1.2, py: 0.9, bgcolor: suporte ? "var(--ops-subtle)" : "var(--ops-primary)", color: suporte ? "var(--ops-text)" : "var(--ops-on-primary)", borderColor: suporte ? "var(--ops-border)" : "var(--ops-primary)" })}>
          <Typography variant="body2" sx={{ fontSize: 11.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{mensagem.texto}</Typography>
        </Paper>
      </Box>
    </Stack>
  );
}
